import React from 'react';
import LinkIcon from '@material-ui/icons/Link';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@material-ui/core';
import { Form, Formik, FormikValues } from 'formik';

import { GET_DEFAULT_LINK, GET_SUPPORTED_LINKS, UPDATE_SHOW_LINK } from 'graphql/show';
import { Link, LinkType } from 'types';
import { FormikInput, Loading } from 'components';
import useGlobalStyles from 'hooks/useGlobalStyles';
import useShows from 'hooks/useShows';

interface Props {
  link?: Link;
  type: LinkType;
  title: string;
  itunesId: string;
  isOpen: boolean;
  onClose: () => void;
}

enum LinkSources {
  Custom = 'custom',
  Automatic = 'automatic',
}

const emptyLink = {
  slug: '',
  link: '',
  source: LinkSources.Custom,
};

const LinksDialog: React.FC<Props> = (props) => {
  const { link, itunesId, isOpen, onClose, title, type } = props;
  const globalClasses = useGlobalStyles();
  const { selectedShow, selectedShowRss } = useShows();

  // Queries
  const [
    getSupportedLinks,
    { data: supportedLinksData, loading: supportedLinksLoading },
  ] = useLazyQuery(GET_SUPPORTED_LINKS);
  const [getDefaultLink, { data: defaultLinkData, loading: defaultLinkLoading }] = useLazyQuery(
    GET_DEFAULT_LINK,
  );

  // Local State
  const isEditing = !!link;
  const singularTitle = title.split('').slice(0, -1).join('');
  const dialogTitle = isEditing ? `Edit ${singularTitle}` : `Add ${singularTitle}`;
  const [updateLink] = useMutation(UPDATE_SHOW_LINK);
  const initialLinkSource = link?.isCustom ? LinkSources.Custom : LinkSources.Automatic;
  const initialValues = !!link
    ? { slug: link.slug, link: link.link, source: initialLinkSource }
    : emptyLink;
  const initialErrors = isEditing ? {} : emptyLink;
  const platforms = supportedLinksData?.supportedLinks?.map(({ label, slug }: Link) => ({
    label,
    value: slug,
  }));
  const sources = [
    { label: 'Custom', value: LinkSources.Custom },
    { label: 'Automatic', value: LinkSources.Automatic },
  ];

  const getDataForForm = () => {
    if (!isOpen) return;
    if (isEditing) {
      getDefaultLink({
        variables: {
          input: {
            slug: link!.slug,
            itunesId: selectedShow.itunesId,
            title: selectedShow.title,
            publisher: selectedShow.publisher,
            rssFeedUrl: selectedShowRss,
          },
        },
      });
    } else {
      getSupportedLinks({ variables: { type } });
    }
  };

  React.useEffect(getDataForForm, [isEditing, selectedShow, isOpen, type]);

  const handleSourceChange = (
    newSource: LinkSources,
    setFieldValue: (field: string, value: any) => void,
  ) => {
    if (newSource === LinkSources.Automatic) {
      setFieldValue('link', defaultLinkData.linkDefaultUrl);
    } else {
      setFieldValue('link', link?.link || '');
    }
  };

  const handleSubmit = async (values: FormikValues) => {
    const { slug, link, source } = values;
    const isCustom = source === LinkSources.Custom;
    const input = { itunesId, link: { slug, link, isCustom } };
    try {
      await updateLink({ variables: { input } });
    } catch ({ message }) {
      // TODO: toast
      console.error(`[ERROR] Error updating the link: ${message}`);
    } finally {
      onClose();
    }
  };

  const renderForm = () => (
    <Formik
      initialValues={initialValues}
      initialErrors={initialErrors}
      onSubmit={handleSubmit}
      validateOnChange
    >
      {({ isValid, values, isSubmitting, setFieldValue }) => (
        <Form>
          <DialogContent>
            <Grid item>
              {!isEditing && (
                <FormikInput
                  title={singularTitle}
                  name="slug"
                  placeholder={singularTitle}
                  select
                  items={platforms}
                />
              )}
              {isEditing && (
                <FormikInput
                  title="Link Source"
                  name="source"
                  select
                  items={sources}
                  onChange={(e) => handleSourceChange(e.target.value as LinkSources, setFieldValue)}
                />
              )}
            </Grid>
            <Grid item>
              <FormikInput
                title="Link"
                name="link"
                placeholder="URL"
                disabled={values.source === LinkSources.Automatic}
                endAdornment={<LinkIcon />}
              />
            </Grid>
          </DialogContent>
          <DialogActions style={{ justifyContent: 'flex-start' }}>
            <div className={globalClasses.actionButtonGroup}>
              <Button color="primary" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={!isValid || isSubmitting}>
                Save
              </Button>
            </div>
          </DialogActions>
        </Form>
      )}
    </Formik>
  );

  if (!isOpen) return <></>;
  else if (supportedLinksLoading || defaultLinkLoading) return <Loading />;

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={isOpen}
      onClose={onClose}
      aria-labelledby="links-dialog-title"
    >
      <DialogTitle id="links-dialog-title">{dialogTitle}</DialogTitle>
      {renderForm()}
    </Dialog>
  );
};

export default LinksDialog;
